import React, { useState, useEffect } from 'react';
import { EnhancedUser } from '../../types/enhancedUser';
import UserFeatureRoleService, { UserFeatureRole, AvailableFeatureRole } from '../../services/userFeatureRoleService';

interface FeatureRoleAssignmentModalProps {
  user: EnhancedUser;
  isOpen: boolean;
  onClose: () => void;
}

const FeatureRoleAssignmentModal: React.FC<FeatureRoleAssignmentModalProps> = ({
  user,
  isOpen,
  onClose
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [userRoles, setUserRoles] = useState<UserFeatureRole[]>([]);
  const [availableRoles, setAvailableRoles] = useState<AvailableFeatureRole[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Load user's current roles and available roles
  useEffect(() => {
    if (isOpen && user) {
      loadData();
    }
  }, [isOpen, user]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [currentRoles, allRoles] = await Promise.all([
        UserFeatureRoleService.getUserFeatureRoles(String(user.id)),
        UserFeatureRoleService.getAvailableFeatureRoles()
      ]);
      setUserRoles(currentRoles);
      setAvailableRoles(allRoles);
    } catch (err: any) {
      setError(err.message || 'Failed to load role data');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignRole = async (roleId: string) => {
    setSaving(true);
    setError(null);
    try {
      await UserFeatureRoleService.assignFeatureRoleToUser(String(user.id), roleId);
      await loadData(); // Refresh the data
    } catch (err: any) {
      setError(err.message || 'Failed to assign role');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveRole = async (roleId: string) => {
    setSaving(true);
    setError(null);
    try {
      await UserFeatureRoleService.removeFeatureRoleFromUser(String(user.id), roleId);
      await loadData(); // Refresh the data
    } catch (err: any) {
      setError(err.message || 'Failed to remove role');
    } finally {
      setSaving(false);
    }
  };

  const isRoleAssigned = (roleId: string) => {
    return userRoles.some(role => role.role_id === roleId);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: '600px', width: '90%' }}>
        <div className="modal-header">
          <h3>Assign Feature Roles: {user.name}</h3>
          <button onClick={onClose} className="close-btn">×</button>
        </div>
        
        <div className="modal-body">
          <p><strong>User:</strong> {user.email}</p>
          
          {error && (
            <div style={{ 
              backgroundColor: '#fee', 
              color: '#c33', 
              padding: '10px', 
              borderRadius: '4px', 
              marginBottom: '15px' 
            }}>
              {error}
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              Loading roles...
            </div>
          ) : (
            <>
              {/* Current Roles Section */}
              <div style={{ marginBottom: '25px' }}>
                <h4 style={{ marginBottom: '10px', color: '#333' }}>Currently Assigned Roles</h4>
                {userRoles.length === 0 ? (
                  <p style={{ color: '#666', fontStyle: 'italic' }}>No feature roles assigned</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {userRoles.map((role) => (
                      <div key={role.role_id} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '10px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        backgroundColor: '#f9f9f9'
                      }}>
                        <div>
                          <strong>{role.name}</strong>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {role.description}
                          </div>
                          <div style={{ fontSize: '11px', color: '#999' }}>
                            Assigned: {new Date(role.assigned_at).toLocaleDateString()}
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemoveRole(role.role_id)}
                          disabled={saving}
                          style={{
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            cursor: saving ? 'not-allowed' : 'pointer'
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Available Roles Section */}
              <div>
                <h4 style={{ marginBottom: '10px', color: '#333' }}>Available Roles to Assign</h4>
                {availableRoles.length === 0 ? (
                  <p style={{ color: '#666', fontStyle: 'italic' }}>No feature roles available</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {availableRoles
                      .filter(role => !isRoleAssigned(role.id))
                      .map((role) => (
                        <div key={role.id} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '10px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          backgroundColor: '#fff'
                        }}>
                          <div>
                            <strong>{role.name}</strong>
                            <div style={{ fontSize: '12px', color: '#666' }}>
                              {role.description}
                            </div>
                          </div>
                          <button
                            onClick={() => handleAssignRole(role.id)}
                            disabled={saving}
                            style={{
                              backgroundColor: '#28a745',
                              color: 'white',
                              border: 'none',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              cursor: saving ? 'not-allowed' : 'pointer'
                            }}
                          >
                            Assign
                          </button>
                        </div>
                      ))}
                    {availableRoles.filter(role => !isRoleAssigned(role.id)).length === 0 && (
                      <p style={{ color: '#666', fontStyle: 'italic' }}>
                        All available roles are already assigned to this user
                      </p>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button 
            onClick={onClose} 
            className="btn-primary"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Close'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeatureRoleAssignmentModal;